import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Select, DatePicker, Input, message } from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import API from "../../../api";
import CustomTable from "../../../components/Table/CustomTable";
import SupplierPaymentDrawer from "./SupplierDrawer";
import { EditOutlined, InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { getUserRole } from "../../../utils/auth";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { Option } = Select;

const SupplierPayment = () => {
  const [filters, setFilters] = useState({
    supplierId: null,
    search: "",
    fromDate: null,
    toDate: null,
    storeName: null,
  });
  const [paymentsData, setPaymentsData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [supplierData, setSupplierData] = useState({
    openingBalance: 0,
    currentBalance: 0,
    closingBalance: 0,
  });
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [userRole, setUserRole] = useState(null); // Initialize as null to handle async role fetch
  const [pdfLoading, setPdfLoading] = useState(false);

  const columns = [
    {
      title: "Sl. No",
      render: (_, __, index) => (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Date",
      dataIndex: "paymentDate",
      render: (text) => dayjs(text).format("DD/MM/YYYY"),
    },
    { title: "Inv No", dataIndex: "transactionId" },
    { title: "Type", dataIndex: "type", render: (text) => text?.toUpperCase() },
    { title: "Payment Method", dataIndex: "paymentMethod", render: (text) => text?.toUpperCase() },
    { title: "Inv Amount", dataIndex: "total_amount", render: (text) => `₹${text.toLocaleString()}` },
    { title: "Paid", dataIndex: "paid_amount", render: (text) => `₹${text.toLocaleString()}` },
    { title: "Balance", dataIndex: "balance_amount", render: (text) => `₹${text > 0 ? text.toLocaleString() : 0}` },
    { title: "Remarks", dataIndex: "remarks", render: (text) => text || "N/A" },
    {
      title: "Actions",
      render: (_, record) => (
        record.type === "pay" ? (
          <Button icon={<EditOutlined />} onClick={() => openEditDrawer(record)} type="link" />
        ) : (
          <Tooltip title="Edit this payment via the related Purchase">
            <Button icon={<InfoCircleOutlined />} type="link" />
          </Tooltip>
        )
      ),
    },
  ];

  const fetchSuppliers = async () => {
    try {
      // Use paginated endpoint for better performance and consistency
      const { data } = await API.get("/suppliers/paginate", {
        params: { 
          page: 1, 
          limit: 1000, // Get all suppliers for dropdown
          storeName: filters.storeName || undefined,
        },
      });
      
      // Handle both response structures for backward compatibility
      const suppliersList = data.suppliers || data || [];
      
      // Filter out suppliers with missing names and log them for debugging
      const validSuppliers = suppliersList.filter(supplier => {
        if (!supplier.name) {
          console.warn("Supplier with missing name found:", supplier);
          return false;
        }
        return true;
      });
      
      setSuppliers(validSuppliers);
      
      // Remove automatic selection - let user choose manually
      console.log("Fetched suppliers:", validSuppliers.length, "valid suppliers");
    } catch (error) {
      message.error("Failed to fetch suppliers.");
      console.error("Error fetching suppliers:", error);
      setSuppliers([]);
    }
  };

  const fetchStores = async () => {
    try {
      const { data } = await API.get("/stores");
      setStores(data || []);
      return data; // Return data for use in .then
    } catch (error) {
      message.error("Failed to fetch stores.");
      console.error("Error fetching stores:", error);
      return [];
    }
  };

  const fetchPayments = async () => {
    if (!filters.supplierId) {
      // Clear payments data when no supplier is selected
      setPaymentsData([]);
      setSupplierData({
        openingBalance: 0,
        currentBalance: 0,
        closingBalance: 0,
      });
      setPaginationInfo((prev) => ({ ...prev, total: 0 }));
      return;
    }

    setLoading(true);
    try {
      const { data } = await API.get(`/payments/supplier/${filters.supplierId}`, {
        params: {
          page: paginationInfo.current,
          limit: paginationInfo.pageSize,
          search: filters.search || undefined,
          storeName: filters.storeName || undefined,
          fromDate: filters.fromDate ? dayjs(filters.fromDate).format("YYYY-MM-DD") : undefined,
          toDate: filters.toDate ? dayjs(filters.toDate).format("YYYY-MM-DD") : undefined,
        },
      });
      setPaymentsData(data.payments || []);
      setSupplierData({
        openingBalance: data.totals.totalAmount,
        currentBalance: data.totals.paidAmount,
        closingBalance: data.totals.balanceAmount,
      });
      setPaginationInfo((prev) => ({ ...prev, total: data.pagination.total }));
    } catch (error) {
      message.error("Failed to fetch payments.");
      console.error("Error fetching payments:", error);
      // Clear data on error
      setPaymentsData([]);
      setSupplierData({
        openingBalance: 0,
        currentBalance: 0,
        closingBalance: 0,
      });
      setPaginationInfo((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value || null }));
  };

  const handleDateChange = (dates) => {
    setSelectedDates(dates ? [dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD")] : []);
  };

  const applyFilters = () => {
    handleFilterChange("fromDate", selectedDates[0]);
    handleFilterChange("toDate", selectedDates[1]);
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setSelectedDates([]);
    setFilters({ 
      supplierId: null, // Reset supplier selection to null
      search: "", 
      fromDate: null, 
      toDate: null, 
      storeName: filters.storeName // Keep store name for non-admin users
    });
    setPaginationInfo((prev) => ({ ...prev, current: 1 }));
  };

  const openEditDrawer = (payment) => {
    setSelectedPayment(payment);
    setDrawerVisible(true);
  };

  const downloadSupplierStatement = async () => {
    if (!filters.supplierId || !paymentsData || paymentsData.length === 0) {
      message.warning("Please select a supplier and ensure data is available");
      return;
    }

    setPdfLoading(true);
    try {
      console.log("Starting supplier statement PDF generation...");
      
      // Try to import jsPDF
      let jsPDF;
      try {
        const jsPDFModule = await import('jspdf');
        jsPDF = jsPDFModule.default;
        console.log("jsPDF imported successfully");
      } catch (importError) {
        console.error("Failed to import jsPDF:", importError);
        throw new Error("Failed to load PDF library");
      }

      // Try to import autotable
      try {
        await import('jspdf-autotable');
        console.log("jspdf-autotable imported successfully");
      } catch (importError) {
        console.error("Failed to import jspdf-autotable:", importError);
        // Continue without autotable, we'll create a simple table
      }
      
      const doc = new jsPDF();
      console.log("PDF document created");
      
      // Get supplier details
      const selectedSupplier = suppliers.find(s => s._id === filters.supplierId);
      const supplierName = selectedSupplier?.name || "Unknown Supplier";
      
      // Add title
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      doc.text("Supplier Payment Statement", 105, 20, { align: "center" });
      
      // Add supplier info
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Supplier: ${supplierName}`, 20, 35);
      
      // Add date range if selected
      if (filters.fromDate && filters.toDate) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(`Period: ${dayjs(filters.fromDate).format("DD/MM/YYYY")} to ${dayjs(filters.toDate).format("DD/MM/YYYY")}`, 20, 45);
      }
      
      // Add generation date
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 55);
      
      // Add summary section with simple table format
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Summary", 105, 75, { align: "center" });
      
      // Create summary table with dashed borders
      const summaryY = 90;
      const summaryRowHeight = 9;
      const summaryColWidth = 60;
      const summaryStartX = 20;
      
      // Summary table header
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(100, 100, 100);
      doc.setLineDashPattern([3, 2], 0);
      doc.rect(summaryStartX, summaryY, summaryColWidth * 3, summaryRowHeight, 'S');
      doc.text("Total Balance", summaryStartX + summaryColWidth/2, summaryY + 8, { align: "center" });
      doc.text("Paid Amount", summaryStartX + summaryColWidth + summaryColWidth/2, summaryY + 8, { align: "center" });
      doc.text("Remaining Balance", summaryStartX + summaryColWidth * 2 + summaryColWidth/2, summaryY + 8, { align: "center" });
      
      // Summary table data row
      const dataY = summaryY + summaryRowHeight;
      doc.setDrawColor(100, 100, 100);
      doc.setLineDashPattern([3, 2], 0);
      doc.rect(summaryStartX, dataY, summaryColWidth * 3, summaryRowHeight, 'S');
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(0, 0, 0);
      doc.text(`${supplierData.openingBalance.toLocaleString()}`, summaryStartX + summaryColWidth/2, dataY + 8, { align: "center" });
      doc.text(`${supplierData.currentBalance.toLocaleString()}`, summaryStartX + summaryColWidth + summaryColWidth/2, dataY + 8, { align: "center" });
      doc.text(`${supplierData.closingBalance.toLocaleString()}`, summaryStartX + summaryColWidth * 2 + summaryColWidth/2, dataY + 8, { align: "center" });
      doc.setLineDashPattern([], 0);

      // Add extra gap before the transaction table
      const tableStartY = dataY + summaryRowHeight + 15;

      // Check if autoTable is available
      if (typeof doc.autoTable === 'function') {
        console.log("Using autoTable for PDF generation");
        
        // Prepare table data
        const tableData = paymentsData.map((item, index) => [
          (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
          dayjs(item.paymentDate).format("DD/MM/YYYY"),
          item.transactionId || "N/A",
          item.type?.toUpperCase() || "N/A",
          item.paymentMethod?.toUpperCase() || "N/A",
          `₹${(item.total_amount || 0).toLocaleString()}`,
          `₹${(item.paid_amount || 0).toLocaleString()}`,
          `₹${(item.balance_amount > 0 ? item.balance_amount : 0).toLocaleString()}`,
          item.remarks || "N/A"
        ]);

        // Add table using autoTable
        doc.autoTable({
          startY: tableStartY,
          head: [["Sl. No", "Date", "Inv No", "Type", "Payment Method", "Inv Amount", "Paid", "Balance", "Remarks"]],
          body: tableData,
          theme: "grid",
          headStyles: {
            fillColor: [102, 126, 234],
            textColor: 255,
            fontStyle: "bold",
            fontSize: 8,
            cellPadding: 1,
            minCellHeight: 5
          },
          styles: {
            fontSize: 9,
            cellPadding: 2,
            lineColor: [200, 200, 200],
            lineWidth: 0.1
          },
          margin: { top: tableStartY, left: 20, right: 20 },
          bodyStyles: {
            valign: 'middle',
          },
          didDrawPage: function(data) {
            // Add page number
            doc.setFontSize(10);
            doc.text(`Page ${doc.internal.getNumberOfPages()}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
          },
          didDrawCell: function(data) {
            // Add light borders to all cells
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.1);
          },
          willDrawCell: function(data) {
            // Add a small break after the header
            if (data.section === 'body' && data.row.index === 0 && data.column.index === 0) {
              data.cell.y += 2;
            }
          }
        });
      } else {
        console.log("autoTable not available, creating simple table");
        
        // Create simple table manually
        let yPosition = tableStartY; // Increased from 120 to provide more space
        const lineHeight = 5;
        let pageNumber = 1;
        
        // Draw table header with borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        
        // Header row background
        doc.setFillColor(102, 126, 234);
        doc.rect(20, yPosition - 4, 170, lineHeight + 2, 'F');
        
        // Header text
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(255, 255, 255);
        doc.text("Sl. No", 25, yPosition);
        doc.text("Date", 40, yPosition);
        doc.text("Inv No", 65, yPosition);
        doc.text("Type", 95, yPosition);
        doc.text("Amount", 115, yPosition);
        doc.text("Paid", 145, yPosition);
        doc.text("Balance", 165, yPosition);
        
        yPosition += lineHeight + 2; // Add a small break after header
        
        // Data rows with borders
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(0, 0, 0);
        
        paymentsData.forEach((item, index) => {
          if (yPosition > 250) {
            // Add page number to current page
            doc.setFontSize(10);
            doc.text(`Page ${pageNumber}`, 20, 280);
            
            doc.addPage();
            pageNumber++;
            yPosition = 20;
            
            // Add header to new page with borders
            doc.setFillColor(102, 126, 234);
            doc.rect(20, yPosition - 4, 170, lineHeight + 2, 'F');
            
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(255, 255, 255);
            doc.text("Sl. No", 25, yPosition);
            doc.text("Date", 40, yPosition);
            doc.text("Inv No", 65, yPosition);
            doc.text("Type", 95, yPosition);
            doc.text("Amount", 115, yPosition);
            doc.text("Paid", 145, yPosition);
            doc.text("Balance", 165, yPosition);
            yPosition += lineHeight;
            
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(0, 0, 0);
          }
          
          // Alternate row background
          if (index % 2 === 1) {
            doc.setFillColor(248, 249, 250);
            doc.rect(20, yPosition - 2, 170, lineHeight, 'F');
          }
          
          const slNo = (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1;
          const date = dayjs(item.paymentDate).format("DD/MM/YYYY");
          const invNo = item.transactionId || "N/A";
          const type = item.type?.toUpperCase() || "N/A";
          const amount = (item.total_amount || 0).toLocaleString();
          const paid = (item.paid_amount || 0).toLocaleString();
          const balance = (item.balance_amount > 0 ? item.balance_amount : 0).toLocaleString();
          
          doc.text(slNo.toString(), 25, yPosition);
          doc.text(date, 40, yPosition);
          doc.text(invNo, 65, yPosition);
          doc.text(type, 95, yPosition);
          doc.text(amount, 115, yPosition);
          doc.text(paid, 145, yPosition);
          doc.text(balance, 165, yPosition);
          
          // Draw row border
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.1);
          doc.line(20, yPosition + 2, 190, yPosition + 2);
          
          yPosition += lineHeight;
        });
        
        // Add page number to last page
        doc.setFontSize(10);
        doc.text(`Page ${pageNumber}`, 20, 280);
      }

      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Supplier_Statement_${supplierName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.pdf`;

      console.log("Saving PDF with filename:", filename);
      
      // Download PDF
      doc.save(filename);
      message.success("Supplier statement downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error(`Failed to generate PDF: ${error.message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  // Fetch user role and stores on component mount
  useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    console.log("role", role);

    fetchStores().then((storeData) => {
      console.log("stores", storeData); // Log stores after fetch completes
      if (role.role !== "admin" && role.storeId) {
        // Find the store name for the user's storeId
        const userStore = storeData.find((store) => store._id === role.storeId);
        if (userStore) {
          handleFilterChange("storeName", userStore.name);
        } else {
          console.warn("No store found for storeId:", role.storeId);
        }
      }
    });
  }, []); // Empty dependency array to run only on mount

  // Fetch suppliers when storeName changes
  useEffect(() => {
    fetchSuppliers();
  }, [filters.storeName]);

  // Debug: Log suppliers data for troubleshooting
  useEffect(() => {
    if (suppliers.length > 0) {
      console.log("Current suppliers in state:", suppliers);
      console.log("Suppliers with missing names:", suppliers.filter(s => !s.name));
    }
  }, [suppliers]);

  // Fetch payments when filters or pagination change
  useEffect(() => {
    fetchPayments();
  }, [filters, paginationInfo.current, paginationInfo.pageSize]);

  return (
    <div
      style={{
        padding: "24px",
        background: "#f5f7fa",
        minHeight: "100vh",
        borderRadius: "12px",
      }}
    >
      {/* Header Section */}
      <div style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "5px",
        marginBottom: "10px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        border: "1px solid #f0f0f0"
      }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 0 }}>
          <Col xs={24} sm={12} md={6}>
            <h2 style={{ 
              margin: 0, 
              fontSize: "24px", 
              fontWeight: "700", 
              color: "#1a1a1a",
              display: "flex",
              alignItems: "center",
              gap: "12px"
            }}>
              <span style={{ 
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                width: "4px",
                height: "32px",
                borderRadius: "2px"
              }}></span>
              Supplier Payments
            </h2>
          </Col>
          <Col>
            <div style={{ 
              display: "flex", 
              gap: "5px", 
              alignItems: "center",
              flexWrap: "wrap"
            }}>
              <div style={{
                background: "linear-gradient(135deg, #667eea 0%,rgb(55, 43, 67) 100%)",
                padding: "10px 20px",
                borderRadius: "12px",
                color: "white",
                minWidth: "160px",
                textAlign: "center",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)"
              }}>
                <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px" }}>
                  Total Balance
                </div>
                <div style={{ 
                  fontSize: "18px", 
                  fontWeight: "700",
                  color: "white"
                }}>
                  ₹{supplierData.openingBalance.toLocaleString()}
                </div>
              </div>

              <div style={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                padding: "10px 20px",
                borderRadius: "12px",
                color: "white",
                minWidth: "160px",
                textAlign: "center",
                boxShadow: "0 4px 12px rgba(240, 147, 251, 0.3)"
              }}>
                <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px" }}>
                  Paid Amount
                </div>
                <div style={{ 
                  fontSize: "18px", 
                  fontWeight: "700",
                  color: "white"
                }}>
                  ₹{supplierData.currentBalance.toLocaleString()}
                </div>
              </div>

              <div style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                padding: "10px 20px",
                borderRadius: "12px",
                color: "white",
                minWidth: "160px",
                textAlign: "center",
                boxShadow: "0 4px 12px rgba(79, 172, 254, 0.3)"
              }}>
                <div style={{ fontSize: "12px", opacity: 0.9, marginBottom: "4px" }}>
                  Balance Amount
                </div>
                <div style={{ 
                  fontSize: "18px", 
                  fontWeight: "700",
                  color: "white"
                }}>
                  ₹{supplierData.closingBalance.toLocaleString()}
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Filters Section */}
      <div style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "10px",
        marginBottom: "10px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
        border: "1px solid #f0f0f0"
      }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8}>
            <div style={{ marginBottom: "8px", fontWeight: "500", color: "#595959" }}>
              Select Supplier
            </div>
            <Select
              placeholder="Choose a supplier to view payments"
              value={filters.supplierId}
              onChange={(value) => handleFilterChange("supplierId", value)}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: "100%" }}
              loading={loading}
              notFoundContent={
                suppliers.length === 0 ? "No suppliers found" : "No matching suppliers"
              }
              size="large"
            >
              {suppliers.map((supplier) => (
                <Option key={supplier._id} value={supplier._id}>
                  {supplier.name || `Supplier ${supplier._id.slice(-6)}`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <div style={{ marginBottom: "8px", fontWeight: "500", color: "#595959" }}>
              Date Range
            </div>
            <Row gutter={8}>
              <Col span={14}>
                <RangePicker
                  value={selectedDates.length ? [dayjs(selectedDates[0]), dayjs(selectedDates[1])] : []}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                  size="large"
                />
              </Col>
              <Col span={5}>
                <Button 
                  type="primary" 
                  onClick={applyFilters}
                  size="large"
                  style={{ width: "100%" }}
                >
                  Filter
                </Button>
              </Col>
              <Col span={5}>
                <Button 
                  onClick={resetFilters} 
                  size="large"
                  style={{ width: "100%" }}
                >
                  Reset
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs={24} sm={6}>
            <div style={{ marginBottom: "8px", fontWeight: "500", color: "#595959" }}>
              Select Store
            </div>
            <Select
              showSearch
              placeholder="Choose store"
              value={filters.storeName}
              onChange={(value) => handleFilterChange("storeName", value)}
              style={{ width: "100%" }}
              allowClear
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={userRole?.role !== "admin"}
              size="large"
            >
              {stores.map((store) => (
                <Option key={store._id} value={store.name}>
                  {store.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      </div>

      <Card
        title={
          <Row justify="space-between" align="middle" style={{padding: "5px"}}>
            <Col>
              <h4 style={{ 
                margin: 0, 
                fontSize: "18px", 
                fontWeight: "600", 
                color: "#1a1a1a" 
              }}>
                Transactions
              </h4>
            </Col>
            <Col>
              <div style={{ marginTop: "12px", textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={downloadSupplierStatement}
                  loading={pdfLoading}
                  disabled={!filters.supplierId || pdfLoading}
                  title={!filters.supplierId ? "Please select a supplier first" : "Download supplier statement"}
                  style={{
                    height: "40px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    boxShadow: "0 2px 8px rgba(52, 199, 89, 0.3)",
                    marginRight: "8px",
                    backgroundColor: "#34c759",
                    borderColor: "#34c759"
                  }}
                >
                  {pdfLoading ? "Generating..." : "Download Statement"}
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedPayment(null);
                    setDrawerVisible(true);
                  }}
                  disabled={!filters.supplierId}
                  title={!filters.supplierId ? "Please select a supplier first" : "Make a payment"}
                  style={{
                    height: "40px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)"
                  }}
                >
                  Make a Payment
                </Button>
              </div>
            </Col>
          </Row>
        }
        style={{
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: "1px solid #f0f0f0"
        }}
        bodyStyle={{ padding: "24px" }}
      >
        {!filters.supplierId ? (
          <div style={{ 
            textAlign: "center", 
            padding: "60px 20px",
            color: "#8c8c8c",
            fontSize: "16px",
            background: "#fafafa",
            borderRadius: "8px",
            border: "2px dashed #d9d9d9"
          }}>
            <div style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}>📊</div>
            <div style={{ fontWeight: "500", marginBottom: "8px" }}>
              No Supplier Selected
            </div>
            <div style={{ fontSize: "14px", opacity: 0.7 }}>
              Please select a supplier from the dropdown above to view payment transactions
            </div>
          </div>
        ) : (
          <div style={{
            background: "#fff",
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #f0f0f0"
          }}>
            <CustomTable
              columns={columns}
              dataSource={paymentsData}
              onPaginationChange={(pagination) =>
                setPaginationInfo({
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                })
              }
              loading={loading}
              total={paginationInfo.total}
              style={{
                borderRadius: "8px",
                overflow: "hidden"
              }}
            />
          </div>
        )}
      </Card>

      <SupplierPaymentDrawer
        open={drawerVisible}
        setDrawerVisible={setDrawerVisible}
        selectedPayment={selectedPayment}
        handleSubmit={fetchPayments}
        selectedSupplierData={selectedPayment ? selectedPayment.supplierId : filters.supplierId}
      />
    </div>
  );
};

export default SupplierPayment;